import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from '../ui/Modal';
import {
  UserIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const CustomerDetails = ({ isOpen, onClose, customer, onCustomerUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'orders', 'analytics'

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      
      // Fetch complete customer details
      const [customerRes, ordersRes, analyticsRes] = await Promise.all([
        axios.get(`/admin/customers/${customer.id}`),
        axios.get(`/admin/customers/${customer.id}/orders`),
        axios.get(`/admin/customers/${customer.id}/analytics`)
      ]).catch(() => [
        // Mock data fallback
        { data: generateMockCustomerData() },
        { data: { orders: generateMockOrders() } },
        { data: generateMockAnalytics() }
      ]);

      setCustomerData(customerRes.data);
      setOrders(ordersRes.data.orders || []);
      setAnalytics(analyticsRes.data);
      
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && customer) {
      loadCustomerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customer]);

  const generateMockCustomerData = () => ({
    ...customer,
    // Enhanced customer data
    addresses: [
      {
        id: 1,
        type: 'billing',
        street: 'רחוב הרצל 123',
        city: 'תל אביב',
        postal_code: '61231',
        country: 'Israel',
        is_default: true
      },
      {
        id: 2,
        type: 'shipping',
        street: 'רחוב בן יהודה 45',
        city: 'תל אביב',
        postal_code: '61232',
        country: 'Israel',
        is_default: false
      }
    ],
    preferences: {
      newsletter_subscribed: true,
      marketing_consent: true,
      preferred_language: 'he',
      communication_method: 'email'
    },
    tags: ['VIP', 'חוזר', 'מומלץ'],
    notes: 'לקוח VIP - שירות מועדף. רגיש למחירים אך נאמן למותג.',
    credit_limit: customer.type === 'b2b' ? 50000 : null,
    payment_terms: customer.type === 'b2b' ? 'NET30' : null
  });

  const generateMockOrders = () => {
    const orders = [];
    const statuses = ['completed', 'pending', 'shipped', 'cancelled'];
    
    for (let i = 1; i <= 8; i++) {
      const orderDate = new Date(Date.now() - (i * 15 * 24 * 60 * 60 * 1000));
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      
      orders.push({
        id: 1000 + i,
        order_number: `ORD-${(1000 + i).toString().padStart(6, '0')}`,
        status: status,
        total_amount: Math.floor(Math.random() * 2000) + 100,
        items_count: Math.floor(Math.random() * 5) + 1,
        created_at: orderDate.toISOString(),
        products: [
          { name: `מוצר ${i}`, quantity: Math.floor(Math.random() * 3) + 1 }
        ]
      });
    }
    
    return orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const generateMockAnalytics = () => ({
    totalSpent: customer.total_spent || 0,
    totalOrders: customer.total_orders || 0,
    averageOrderValue: customer.total_orders ? (customer.total_spent / customer.total_orders) : 0,
    lastOrderDate: customer.last_order_date,
    favoriteCategories: [
      { name: 'אלקטרוניקה', count: 12, percentage: 40 },
      { name: 'ביגוד ואופנה', count: 8, percentage: 27 },
      { name: 'בית וגינה', count: 5, percentage: 17 },
      { name: 'אחרים', count: 5, percentage: 16 }
    ],
    monthlySpending: [
      { month: 'ינואר', amount: 1200 },
      { month: 'פברואר', amount: 850 },
      { month: 'מרץ', amount: 1500 },
      { month: 'אפריל', amount: 920 },
      { month: 'מאי', amount: 1100 },
      { month: 'יוני', amount: 1300 }
    ],
    satisfactionScore: Math.floor(Math.random() * 3) + 3, // 3-5 stars
    loyaltyLevel: customer.total_orders > 10 ? 'זהב' : customer.total_orders > 5 ? 'כסף' : 'ברונזה'
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      shipped: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      completed: 'הושלם',
      pending: 'בהמתנה',
      shipped: 'נשלח',
      cancelled: 'בוטל'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getLoyaltyColor = (level) => {
    const colors = {
      'זהב': 'text-yellow-600 bg-yellow-100',
      'כסף': 'text-gray-600 bg-gray-100',
      'ברונזה': 'text-orange-600 bg-orange-100'
    };
    return colors[level] || 'text-gray-600 bg-gray-100';
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        className={`h-4 w-4 ${
          index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (!customerData || loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="טוען..." size="full">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${customerData.first_name} ${customerData.last_name}`}
      size="full"
    >
      <div className="space-y-6">
        
        {/* Customer Header */}
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-6 rounded-lg border border-primary-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex-shrink-0">
                <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {customerData.first_name.charAt(0)}
                  </span>
                </div>
              </div>
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {customerData.first_name} {customerData.last_name}
                  </h2>
                  {customerData.type === 'b2b' ? (
                    <BuildingOfficeIcon className="h-6 w-6 text-purple-500" />
                  ) : (
                    <UserIcon className="h-6 w-6 text-green-500" />
                  )}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    customerData.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {customerData.status === 'active' ? 'פעיל' : 'לא פעיל'}
                  </span>
                </div>
                {customerData.company_name && (
                  <p className="text-lg text-blue-600 mb-1">{customerData.company_name}</p>
                )}
                <div className="flex items-center text-gray-600 space-x-4">
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-4 w-4 ml-1" />
                    <span>{customerData.email}</span>
                  </div>
                  <div className="flex items-center">
                    <PhoneIcon className="h-4 w-4 ml-1" />
                    <span>{customerData.phone}</span>
                  </div>
                  {analytics && (
                    <div className="flex items-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLoyaltyColor(analytics.loyaltyLevel)}`}>
                        רמת נאמנות: {analytics.loyaltyLevel}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button className="btn btn-outline flex items-center">
                <PencilIcon className="h-4 w-4 ml-2" />
                ערוך
              </button>
              <button className="btn btn-outline flex items-center">
                <EnvelopeIcon className="h-4 w-4 ml-2" />
                שלח אימייל
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="mr-3">
                <p className="text-sm font-medium text-gray-600">סך הוצאה</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(analytics?.totalSpent || 0)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShoppingBagIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="mr-3">
                <p className="text-sm font-medium text-gray-600">הזמנות</p>
                <p className="text-2xl font-bold text-gray-900">
                  {analytics?.totalOrders || 0}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ChartBarIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="mr-3">
                <p className="text-sm font-medium text-gray-600">ממוצע הזמנה</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(analytics?.averageOrderValue || 0)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CalendarIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="mr-3">
                <p className="text-sm font-medium text-gray-600">הזמנה אחרונה</p>
                <p className="text-sm font-bold text-gray-900">
                  {analytics?.lastOrderDate ? formatDate(analytics.lastOrderDate) : 'אין הזמנות'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'סקירה כללית', icon: UserIcon },
              { id: 'orders', label: 'הזמנות', icon: ShoppingBagIcon },
              { id: 'analytics', label: 'אנליטיקה', icon: ChartBarIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 ml-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-96">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Customer Information */}
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <UserIcon className="h-5 w-5 ml-2" />
                    פרטי לקוח
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">סוג לקוח:</span>
                      <span className="font-medium">{customerData.type === 'b2b' ? 'B2B' : 'B2C'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">תאריך הצטרפות:</span>
                      <span className="font-medium">{formatDate(customerData.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">עדכון אחרון:</span>
                      <span className="font-medium">{formatDate(customerData.updatedAt)}</span>
                    </div>
                    {customerData.type === 'b2b' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">תפקיד:</span>
                          <span className="font-medium">{customerData.job_title || 'לא צוין'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">מס׳ עוסק:</span>
                          <span className="font-medium">{customerData.tax_id || 'לא צוין'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Addresses */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MapPinIcon className="h-5 w-5 ml-2" />
                    כתובות
                  </h3>
                  <div className="space-y-4">
                    {customerData.addresses?.map((address) => (
                      <div key={address.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {address.type === 'billing' ? 'כתובת חיוב' : 'כתובת משלוח'}
                          </span>
                          {address.is_default && (
                            <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                              ברירת מחדל
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700">
                          {address.street}<br />
                          {address.city}, {address.postal_code}<br />
                          {address.country}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="space-y-6">
                {/* Tags */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">תגיות</h3>
                  <div className="flex flex-wrap gap-2">
                    {customerData.tags?.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Preferences */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">העדפות</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">ניוזלטר:</span>
                      {customerData.preferences?.newsletter_subscribed ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">הסכמה לשיווק:</span>
                      {customerData.preferences?.marketing_consent ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">שפה מועדפת:</span>
                      <span className="font-medium">עברית</span>
                    </div>
                  </div>
                </div>

                {/* Satisfaction Score */}
                {analytics && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">דירוג שביעות רצון</h3>
                    <div className="flex items-center space-x-2">
                      <div className="flex">
                        {renderStars(analytics.satisfactionScore)}
                      </div>
                      <span className="text-sm text-gray-600">
                        ({analytics.satisfactionScore}/5)
                      </span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">הערות</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {customerData.notes || 'אין הערות'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">היסטוריית הזמנות</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        הזמנה
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        תאריך
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        פריטים
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        סכום
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        סטטוס
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        פעולות
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-primary-600">
                            {order.order_number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(order.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.items_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(order.total_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-primary-600 hover:text-primary-900">
                            צפה
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && analytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Favorite Categories */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">קטגוריות מועדפות</h3>
                <div className="space-y-4">
                  {analytics.favoriteCategories.map((category, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                      <div className="flex items-center space-x-3">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full"
                            style={{ width: `${category.percentage}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 w-12 text-left">
                          {category.percentage}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Spending */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">הוצאות חודשיות</h3>
                <div className="space-y-3">
                  {analytics.monthlySpending.map((month, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{month.month}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(month.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <div className="flex space-x-3">
            <button className="btn btn-outline flex items-center">
              <PencilIcon className="h-4 w-4 ml-2" />
              ערוך לקוח
            </button>
            <button className="btn btn-outline flex items-center">
              <EnvelopeIcon className="h-4 w-4 ml-2" />
              שלח אימייל
            </button>
            <button className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50 flex items-center">
              <TrashIcon className="h-4 w-4 ml-2" />
              מחק לקוח
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="btn btn-primary"
          >
            סגור
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomerDetails;
